/**
 * Divi Table Builder Pro Plus - Frontend JavaScript
 * Handles interactive features: sorting, search, pagination
 */

import './components/table-builder/frontend.scss';

// Types
interface TableSettings {
    interactive: {
        sorting: { enabled: boolean; sortableColumns?: string[] };
        search: { enabled: boolean; liveSearch?: boolean; highlightResults?: boolean };
        pagination: { enabled: boolean; rowsPerPage?: number };
    };
    responsive: {
        mobile: { layout: string };
    };
    i18n: Record<string, string>;
}

interface TableState {
    currentPage: number;
    sortColumn: number | null;
    sortDirection: 'asc' | 'desc';
    searchQuery: string;
    visibleRows: HTMLTableRowElement[];
}

// Initialize all table modules on the page
const initTableModules = (): void => {
    const tables = document.querySelectorAll<HTMLElement>('.dtbpp-table-module');
    tables.forEach((module) => initTableModule(module));
};

// Initialize a single table module
const initTableModule = (module: HTMLElement): void => {
    const settingsData = module.dataset.settings;
    if (!settingsData) return;

    let settings: TableSettings;
    try {
        settings = JSON.parse(settingsData);
    } catch (e) {
        console.warn('DTBPP: Invalid settings data');
        return;
    }

    const table = module.querySelector<HTMLTableElement>('.dtbpp-table');
    if (!table) return;

    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const state: TableState = {
        currentPage: 1,
        sortColumn: null,
        sortDirection: 'asc',
        searchQuery: '',
        visibleRows: Array.from(tbody.querySelectorAll('tr')),
    };

    // Interactive features disabled for now
    const sortingEnabled = false;
    const searchEnabled = false;
    const paginationEnabled = false;

    // Initialize sorting
    if (sortingEnabled && settings.interactive?.sorting?.enabled) {
        initSorting(table, state, settings);
    }

    // Initialize search
    if (searchEnabled && settings.interactive?.search?.enabled) {
        initSearch(module, table, state, settings);
    }

    // Initialize pagination
    if (paginationEnabled && settings.interactive?.pagination?.enabled) {
        initPagination(module, table, state, settings);
    }
};

// Sorting functionality
const initSorting = (
    table: HTMLTableElement,
    state: TableState,
    settings: TableSettings
): void => {
    const headers = table.querySelectorAll<HTMLTableCellElement>('thead th');

    headers.forEach((header, index) => {
        header.classList.add('dtbpp-sortable');
        header.setAttribute('role', 'button');
        header.setAttribute('tabindex', '0');
        header.setAttribute('aria-label', settings.i18n?.sortAsc || 'Sort ascending');

        const handleSort = () => {
            const newDirection =
                state.sortColumn === index && state.sortDirection === 'asc' ? 'desc' : 'asc';

            state.sortColumn = index;
            state.sortDirection = newDirection;

            sortTable(table, index, newDirection);

            // Update header classes and aria
            headers.forEach((h) => {
                h.classList.remove('dtbpp-sort-asc', 'dtbpp-sort-desc');
                h.setAttribute('aria-sort', 'none');
            });
            header.classList.add(`dtbpp-sort-${newDirection}`);
            header.setAttribute('aria-sort', newDirection === 'asc' ? 'ascending' : 'descending');
        };

        header.addEventListener('click', handleSort);
        header.addEventListener('keypress', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSort();
            }
        });
    });
};

// Sort table rows
const sortTable = (
    table: HTMLTableElement,
    columnIndex: number,
    direction: 'asc' | 'desc'
): void => {
    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const rows = Array.from(tbody.querySelectorAll('tr'));

    rows.sort((a, b) => {
        const cellA = a.cells[columnIndex]?.textContent?.trim() || '';
        const cellB = b.cells[columnIndex]?.textContent?.trim() || '';

        // Check if numeric
        const numA = parseFloat(cellA.replace(/[^0-9.-]/g, ''));
        const numB = parseFloat(cellB.replace(/[^0-9.-]/g, ''));

        if (!isNaN(numA) && !isNaN(numB)) {
            return direction === 'asc' ? numA - numB : numB - numA;
        }

        // String comparison
        const comparison = cellA.localeCompare(cellB, undefined, { sensitivity: 'base' });
        return direction === 'asc' ? comparison : -comparison;
    });

    // Re-append rows in sorted order
    rows.forEach((row) => tbody.appendChild(row));

    // Re-apply striping
    rows.forEach((row, index) => {
        row.classList.remove('dtbpp-row-odd', 'dtbpp-row-even');
        row.classList.add(index % 2 === 0 ? 'dtbpp-row-even' : 'dtbpp-row-odd');
    });
};

// Search functionality
const initSearch = (
    module: HTMLElement,
    table: HTMLTableElement,
    state: TableState,
    settings: TableSettings
): void => {
    const searchInput = module.querySelector<HTMLInputElement>('.dtbpp-search-input');
    if (!searchInput) return;

    const performSearch = () => {
        const query = searchInput.value.toLowerCase().trim();
        state.searchQuery = query;

        const tbody = table.querySelector('tbody');
        if (!tbody) return;

        const rows = Array.from(tbody.querySelectorAll('tr'));
        state.visibleRows = [];

        rows.forEach((row) => {
            const cells = Array.from(row.querySelectorAll('td'));
            const text = cells.map((c) => c.textContent?.toLowerCase() || '').join(' ');

            if (query === '' || text.includes(query)) {
                row.classList.remove('dtbpp-hidden');
                state.visibleRows.push(row);

                // Highlight results if enabled
                if (settings.interactive.search.highlightResults && query) {
                    cells.forEach((cell) => {
                        // Remove existing highlights
                        cell.innerHTML = cell.innerHTML.replace(/<mark[^>]*>(.*?)<\/mark>/gi, '$1');

                        // Add new highlights
                        if (query && cell.textContent?.toLowerCase().includes(query)) {
                            const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
                            cell.innerHTML = cell.innerHTML.replace(regex, '<mark>$1</mark>');
                        }
                    });
                }
            } else {
                row.classList.add('dtbpp-hidden');
            }
        });

        // Update pagination if enabled
        if (settings.interactive?.pagination?.enabled) {
            state.currentPage = 1;
            updatePagination(module, table, state, settings);
        }
    };

    if (settings.interactive.search.liveSearch !== false) {
        let debounceTimer: number;
        searchInput.addEventListener('input', () => {
            clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(performSearch, 200);
        });
    } else {
        searchInput.addEventListener('change', performSearch);
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                performSearch();
            }
        });
    }
};

// Pagination functionality
const initPagination = (
    module: HTMLElement,
    table: HTMLTableElement,
    state: TableState,
    settings: TableSettings
): void => {
    const paginationWrapper = module.querySelector('.dtbpp-pagination-wrapper');
    if (!paginationWrapper) return;

    updatePagination(module, table, state, settings);
};

// Update pagination UI
const updatePagination = (
    module: HTMLElement,
    table: HTMLTableElement,
    state: TableState,
    settings: TableSettings
): void => {
    const paginationWrapper = module.querySelector('.dtbpp-pagination-wrapper');
    if (!paginationWrapper) return;

    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    const rowsPerPage = settings.interactive.pagination.rowsPerPage || 10;
    const visibleRows = state.visibleRows.length > 0
        ? state.visibleRows
        : Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr:not(.dtbpp-hidden)'));
    const totalRows = visibleRows.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);

    // Clamp current page
    if (state.currentPage > totalPages) {
        state.currentPage = Math.max(1, totalPages);
    }

    // Hide/show rows based on current page
    const startIndex = (state.currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    visibleRows.forEach((row, index) => {
        if (index >= startIndex && index < endIndex) {
            row.style.display = '';
        } else {
            row.style.display = 'none';
        }
    });

    // Update pagination UI
    const i18n = settings.i18n || {};

    paginationWrapper.innerHTML = `
    <div class="dtbpp-pagination-info">
      ${i18n.showing || 'Showing'} ${Math.min(startIndex + 1, totalRows)} - ${Math.min(endIndex, totalRows)} 
      ${i18n.of || 'of'} ${totalRows} ${i18n.entries || 'entries'}
    </div>
    <div class="dtbpp-pagination-buttons">
      <button class="dtbpp-pagination-btn dtbpp-prev" ${state.currentPage <= 1 ? 'disabled' : ''}>
        ${i18n.previous || 'Previous'}
      </button>
      ${generatePageButtons(state.currentPage, totalPages)}
      <button class="dtbpp-pagination-btn dtbpp-next" ${state.currentPage >= totalPages ? 'disabled' : ''}>
        ${i18n.next || 'Next'}
      </button>
    </div>
  `;

    // Attach event listeners
    const prevBtn = paginationWrapper.querySelector('.dtbpp-prev');
    const nextBtn = paginationWrapper.querySelector('.dtbpp-next');
    const pageButtons = paginationWrapper.querySelectorAll('.dtbpp-page');

    prevBtn?.addEventListener('click', () => {
        if (state.currentPage > 1) {
            state.currentPage--;
            updatePagination(module, table, state, settings);
        }
    });

    nextBtn?.addEventListener('click', () => {
        if (state.currentPage < totalPages) {
            state.currentPage++;
            updatePagination(module, table, state, settings);
        }
    });

    pageButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const page = parseInt((btn as HTMLElement).dataset.page || '1', 10);
            state.currentPage = page;
            updatePagination(module, table, state, settings);
        });
    });
};

// Generate page number buttons
const generatePageButtons = (currentPage: number, totalPages: number): string => {
    const buttons: string[] = [];
    const maxVisible = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
        startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
        buttons.push(`<button class="dtbpp-pagination-btn dtbpp-page" data-page="1">1</button>`);
        if (startPage > 2) {
            buttons.push(`<span class="dtbpp-pagination-ellipsis">...</span>`);
        }
    }

    for (let i = startPage; i <= endPage; i++) {
        const activeClass = i === currentPage ? 'dtbpp-active' : '';
        buttons.push(`<button class="dtbpp-pagination-btn dtbpp-page ${activeClass}" data-page="${i}">${i}</button>`);
    }

    if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
            buttons.push(`<span class="dtbpp-pagination-ellipsis">...</span>`);
        }
        buttons.push(`<button class="dtbpp-pagination-btn dtbpp-page" data-page="${totalPages}">${totalPages}</button>`);
    }

    return buttons.join('');
};

// Utility: escape regex special characters
const escapeRegExp = (string: string): string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTableModules);
} else {
    initTableModules();
}

// Re-initialize on Divi Builder preview update
if (typeof window !== 'undefined') {
    (window as any).ETBuilderBackend?.on?.('et_fb_preview_ready', initTableModules);
}

export { initTableModules };
